<template>
  <v-container fluid class="svc-dashboard">
    <div v-if="currentService">
      <!-- Header -->
      <div class="svc-dashboard__header">
        <div class="svc-dashboard__header-text">
          <div class="svc-dashboard__title-row">
            <h1 class="svc-dashboard__title">{{ currentService.name }}</h1>
            <v-tooltip v-if="eventModuleSettingsRoute" bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  class="svc-dashboard__settings-btn-wrap"
                >
                  <router-link
                    :to="eventModuleSettingsRoute"
                    class="svc-dashboard__settings-btn"
                    aria-label="Go to Event Settings"
                  >
                    <v-icon
                      size="16"
                      color="var(--app-accent)"
                      class="svc-dashboard__settings-btn-icon"
                      >mdi-arrow-right</v-icon
                    >
                  </router-link>
                </span>
              </template>
              <span>Go to Event Settings</span>
            </v-tooltip>
          </div>
          <p class="svc-dashboard__subtitle">
            {{ currentService.description || "Select a module to get started" }}
          </p>
        </div>
      </div>

      <!-- Module cards -->
      <ADashboard :modules="menu" />
    </div>
    <div v-else>
      <ALoader />
    </div>
  </v-container>
</template>

<script>
import Breadcrumb from "@/objects/breadcrumb";
import configMenu from "@/config/menu";
import { mapState } from "vuex";
import hrdcFunction from "@/objects/hrdcFunction";

export default {
  mixins: [hrdcFunction],
  computed: mapState({
    services: (state) => state.service.data.services,
    currentService: (state) => state.service.data.currentService,
    auth: (state) => state.auth.data,
  }),
  data: () => ({
    breadcrumb: new Breadcrumb(),
    service: null,
    menu: [],
    eventModuleSettingsRoute: null,
  }),
  created() {
    var serviceKey = this.$route.params.serviceKey;
    if (serviceKey)
      this.service = this.$_.find(this.services, { key: serviceKey });

    // For hrdc_event_settings, create a virtual service if not found in store
    if (!this.service && serviceKey === "hrdc_event_settings") {
      this.service = {
        key: "hrdc_event_settings",
        name: "Event Settings",
        description: "Event Module management",
      };
    }

    if (!this.service) return;

    this.breadcrumb.add({
      text: "Dashboard",
      to: { name: "PageConsoleDashboard" },
      exact: true,
    });
    this.breadcrumb.add({
      text: "Services",
      to: { name: "PageServiceBrowse" },
      exact: true,
    });
    this.breadcrumb.add({
      text: this.service.name,
      to: {
        name: "PageServiceDashboard",
        params: { serviceKey: this.service.key },
      },
      exact: true,
    });
    var targetMenu = configMenu[this.service.key];
    if (!targetMenu && this.service.key === "hrdc_event_settings") {
      const {
        getAllEventConfigs,
      } = require("@/services/hrdc_event/eventRegistry");
      const firstConfig = getAllEventConfigs()[0];
      targetMenu = [
        // XERO Token moved to the Developer Console (/en/developer).
        {
          name: "Report Analysis",
          icon: "mdi-file-chart",
          route: firstConfig
            ? { name: `${firstConfig.routePrefix}EventSettingsReportAnalysis` }
            : {},
        },
        {
          name: "Event Modules",
          icon: "mdi-puzzle-outline",
          route: firstConfig
            ? {
                name: "HrdcEventSettingsEventModules",
                params: { serviceKey: "hrdc_event_settings" },
              }
            : {},
        },
        {
          name: "Email Reminder",
          icon: "mdi-email-alert-outline",
          beta: true,
          route: firstConfig
            ? {
                name: "HrdcEventSettingsEmailReminder",
                params: { serviceKey: "hrdc_event_settings" },
              }
            : {},
        },
      ];
    }
    if (!targetMenu) {
      const {
        getEventConfig,
        generateMenuConfig,
      } = require("@/services/hrdc_event/eventRegistry");
      const eventConfig = getEventConfig(this.service.key);
      if (eventConfig) {
        targetMenu = generateMenuConfig(eventConfig);
        this.eventModuleSettingsRoute = {
          name: "HrdcEventSettingsEventModules",
          params: { serviceKey: "hrdc_event_settings" },
          query: { from: this.service.key },
        };
      }
    }
    if (targetMenu) this.menu = this.$_.clone(targetMenu);

    const data = {
      roles: this.auth.roles,
      menu: this.menu,
    };

    this.configMenu(data);
  },
};
</script>

<style scoped>
/* ============================================================================
   DARK MODE
   ----------------------------------------------------------------------------
   Hand-authored — do NOT regenerate this from the light rules below. Same
   system as the console dashboard (see the DARK MODE block in
   src/views/console/PageConsoleDashboard.vue): surfaces separate by the ramp
   (page → card → elevated), edges are --app-border rather than the light
   theme's near-white hairline, depth is shadow + a 1px inner top highlight.

   The module cards on this page come from ADashboard.vue, which carries the
   matching block. Colours are token references only. No literal hexes.
   ============================================================================ */

/* Was --app-elevated, which is a raised-surface tone: the page read as one
   giant card with the real cards floating a shade darker than their backdrop. */
.dark-mode .svc-dashboard {
  background-color: var(--app-page);
  background-image: var(--app-page-glow);
}

.dark-mode .svc-dashboard__title {
  color: var(--app-text);
}

.dark-mode .svc-dashboard__subtitle {
  color: var(--app-text-muted);
}

.dark-mode .svc-dashboard__settings-btn {
  background: var(--app-elevated);
  border-color: var(--app-accent-border);
  box-shadow:
    inset 0 1px 0 var(--app-inner-highlight),
    0 2px 8px var(--app-shadow-mid);
}

.dark-mode .svc-dashboard__settings-btn:hover {
  background: var(--app-accent);
  border-color: var(--app-accent);
  box-shadow: 0 4px 14px var(--app-shadow);
}

/* White on the dark-mode accent is barely legible — the accent's own
   foreground token is the near-black that pairs with it. */
.dark-mode .svc-dashboard__settings-btn:hover .svc-dashboard__settings-btn-icon {
  color: var(--app-accent-fg) !important;
  caret-color: var(--app-accent-fg) !important;
}

/* ===== end DARK MODE ===== */

.svc-dashboard {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding: 36px 44px !important;
  font-family: "Lato", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
}

.svc-dashboard__header {
  margin-bottom: 32px;
}

.svc-dashboard__header-text {
  min-width: 0;
}

.svc-dashboard__title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.svc-dashboard__title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.svc-dashboard__subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0;
  font-weight: 400;
}

.svc-dashboard__settings-btn-wrap {
  display: inline-flex;
}

.svc-dashboard__settings-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #ffffff;
  border: 1px solid rgba(14, 165, 233, 0.25);
  border-radius: 8px;
  color: #0ea5e9;
  text-decoration: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.svc-dashboard__settings-btn:hover {
  background: #0ea5e9;
  border-color: #0ea5e9;
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.25);
  transform: translateY(-1px);
}

.svc-dashboard__settings-btn:hover .svc-dashboard__settings-btn-icon {
  color: #ffffff !important;
  caret-color: #ffffff !important;
}

@media (max-width: 599px) {
  .svc-dashboard {
    padding: 20px 16px !important;
  }

  .svc-dashboard__title {
    font-size: 1.2rem;
  }

  .svc-dashboard__settings-btn {
    width: 28px;
    height: 28px;
  }
}
</style>
